import * as Sentry from '@sentry/react';

// ---------------------------------------------------------------------------
// Feature flag
// ---------------------------------------------------------------------------

export function isOnboardingEnabled(): boolean {
  const flag = import.meta.env.VITE_ONBOARDING_ENABLED;
  // Default true unless explicitly disabled
  return flag !== 'false' && flag !== '0';
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type OnboardingRole = 'execution' | 'coordination';

export interface ChecklistStep {
  key: string;
  labelKey: string;
}

export interface OnboardingState {
  completedSteps: string[];
  checklistDismissedAt: string | null;
}

// ---------------------------------------------------------------------------
// Checklist config per role
// ---------------------------------------------------------------------------

export const CHECKLIST_EXECUTION: ChecklistStep[] = [
  { key: 'visit_dashboard', labelKey: 'onboarding.checklist.visitDashboard' },
  { key: 'create_first_record', labelKey: 'onboarding.checklist.createFirstRecord' },
  { key: 'first_upload', labelKey: 'onboarding.checklist.firstUpload' },
  { key: 'visit_monitor', labelKey: 'onboarding.checklist.visitMonitor' },
  { key: 'visit_calendar', labelKey: 'onboarding.checklist.visitCalendar' },
];

export const CHECKLIST_COORDINATION: ChecklistStep[] = [
  { key: 'visit_dashboard', labelKey: 'onboarding.checklist.visitDashboardCoord' },
  { key: 'visit_rotation', labelKey: 'onboarding.checklist.visitRotation' },
  { key: 'create_or_review_list', labelKey: 'onboarding.checklist.createOrReviewList' },
  { key: 'visit_monitor', labelKey: 'onboarding.checklist.visitMonitorCoord' },
  { key: 'visit_settings', labelKey: 'onboarding.checklist.visitSettings' },
];

export function getChecklistForRole(role: OnboardingRole): ChecklistStep[] {
  return role === 'coordination' ? CHECKLIST_COORDINATION : CHECKLIST_EXECUTION;
}

// ---------------------------------------------------------------------------
// Auto-complete route mapping
// ---------------------------------------------------------------------------

/** Maps route pathname to checklist step keys that should auto-complete on pageview */
export const ROUTE_STEP_MAP: Record<string, string[]> = {
  '/': ['visit_dashboard'],
  '/patients': ['create_first_record'], // visiting is not enough — see note below
  '/uploads': ['first_upload'],          // visiting is not enough — see note below
  '/monitor': ['visit_monitor'],
  '/surgical-lists': ['visit_calendar', 'create_or_review_list'],
  '/panoramic-calendar': ['visit_rotation'],
  '/settings': ['visit_settings'],
};

// Steps that require a specific action (POST success), not just a pageview
export const ACTION_STEPS = new Set(['create_first_record', 'first_upload']);

// ---------------------------------------------------------------------------
// localStorage persistence
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'dominoris.onboarding';

function getStorageKey(orgId: number): string {
  return `${STORAGE_KEY}.${orgId}`;
}

export function loadOnboardingState(orgId: number): OnboardingState {
  try {
    const raw = localStorage.getItem(getStorageKey(orgId));
    if (raw) {
      const parsed = JSON.parse(raw) as OnboardingState;
      return {
        completedSteps: Array.isArray(parsed.completedSteps) ? parsed.completedSteps : [],
        checklistDismissedAt: parsed.checklistDismissedAt ?? null,
      };
    }
  } catch { /* corrupted data — reset */ }
  return { completedSteps: [], checklistDismissedAt: null };
}

export function saveOnboardingState(orgId: number, state: OnboardingState): void {
  try {
    localStorage.setItem(getStorageKey(orgId), JSON.stringify(state));
  } catch { /* quota exceeded — silent */ }
}

// ---------------------------------------------------------------------------
// Sentry instrumentation
// ---------------------------------------------------------------------------

export function trackOnboardingBreadcrumb(
  message: string,
  data: Record<string, string | number>,
): void {
  Sentry.addBreadcrumb({
    category: 'onboarding',
    message,
    data,
    level: 'info',
  });
}

export function setOnboardingSentryTags(role: OnboardingRole, progress: string): void {
  Sentry.setTag('onboarding.role', role);
  Sentry.setTag('onboarding.progress', progress);
}

// ---------------------------------------------------------------------------
// Context hints
// ---------------------------------------------------------------------------

export interface HintConfig {
  id: string;
  titleKey: string;
  bodyKey: string;
  coordinationOnly?: boolean;
}

export const HINTS: HintConfig[] = [
  {
    id: 'nav.surgical_lists',
    titleKey: 'onboarding.hints.surgicalLists.title',
    bodyKey: 'onboarding.hints.surgicalLists.body',
  },
  {
    id: 'uploads.dropzone',
    titleKey: 'onboarding.hints.uploadsDropzone.title',
    bodyKey: 'onboarding.hints.uploadsDropzone.body',
  },
  {
    id: 'calendar.filter_level',
    titleKey: 'onboarding.hints.calendarFilterLevel.title',
    bodyKey: 'onboarding.hints.calendarFilterLevel.body',
  },
  {
    id: 'monitor.status',
    titleKey: 'onboarding.hints.monitorStatus.title',
    bodyKey: 'onboarding.hints.monitorStatus.body',
  },
  {
    id: 'settings.levels',
    titleKey: 'onboarding.hints.settingsLevels.title',
    bodyKey: 'onboarding.hints.settingsLevels.body',
    coordinationOnly: true,
  },
];

const HINTS_STORAGE_KEY = 'dominoris.onboarding.hints';

export function loadDismissedHints(): Set<string> {
  try {
    const raw = localStorage.getItem(HINTS_STORAGE_KEY);
    if (raw) {
      const arr = JSON.parse(raw) as string[];
      return new Set(arr);
    }
  } catch { /* corrupted */ }
  return new Set();
}

export function saveDismissedHints(dismissed: Set<string>): void {
  try {
    localStorage.setItem(HINTS_STORAGE_KEY, JSON.stringify([...dismissed]));
  } catch { /* quota exceeded */ }
}

export function isHintsGloballyDismissed(): boolean {
  try {
    return localStorage.getItem(`${HINTS_STORAGE_KEY}.global`) === '1';
  } catch { return false; }
}

export function dismissHintsGlobally(): void {
  try {
    localStorage.setItem(`${HINTS_STORAGE_KEY}.global`, '1');
  } catch { /* quota exceeded */ }
}
